/**
 * Projectiles, damage resolution, and every delivery mechanism a tower owns.
 *
 * One pool, six behaviours. A projectile is a plain pooled object from
 * `core/types.ts` plus a parallel `Extra` record holding the behaviour payload
 * that `core/types.ts` has no field for (cluster counts, fuse radii, well
 * lifetimes). Keeping the extras in a sibling array rather than widening the
 * core type means the enemy and render subsystems can read `Projectile` without
 * knowing anything about napalm.
 *
 * ## Delivery
 *
 * | kind | who | behaviour |
 * |---|---|---|
 * | `bolt` | Turret, Sniper | straight or homing, pierces, hit-set prevents double-dipping |
 * | `pellet` | Flak, Cryo | short-lived cone spray, one pellet per pierce point |
 * | `arc` | Mortar | flies to a fixed ground point, detonates on arrival |
 * | `orb` | Acid | slow homing blob, applies stacking corrosion |
 * | `well` | Singularity | parks on the track, pulls, damages, then collapses |
 * | `chain` | Tesla | hits, then jumps to the nearest untouched neighbour |
 *
 * Laser beams never become projectiles — `hitscan` resolves them inside the
 * same step the tower fires, and only the FX outlive the call.
 *
 * ## Damage order
 *
 * `resolve()` runs exactly one order, and the order matters:
 *
 * 1. type multiplier from `EnemyArchetype.resist`
 * 2. armour-pierce bonus, applied only when the target actually has armour
 * 3. flat armour subtraction, floored at zero
 * 4. critical multiplier
 * 5. shield absorption (with `shieldMelt` applied to the shield pool only)
 * 6. hit points
 *
 * Crits landing after armour is the important one: it means a crit on a heavily
 * armoured target multiplies what got through, not what was fired, so armour
 * stays a real counter to burst towers rather than a rounding error.
 *
 * ## Allocation
 *
 * The pool grows on demand and never shrinks. `world.fx` queues are drained and
 * length-reset by the render layer each frame, so this file hands them pooled
 * objects and resets its own cursor whenever it sees an empty queue.
 */

import { ECONOMY, VIEW } from '@/core/config';
import type { PhosphorName } from '@/core/palette';
import type {
  DamageType, Enemy, ParticleSpawn, Projectile, Shockwave, System, Tower, World,
} from '@/core/types';
import { payoutScale } from '../Economy';
import { deathBurst } from '../entities/Enemy';
import { EnemyIndex, NO_TARGET } from './Targeting';

// ─────────────────────────────────────────────────────────────────── fx pool ──

/**
 * Pooled writer for the three `world.fx` queues.
 *
 * `World.fx.particles` is an array of objects and the contract is that the
 * render layer drains it and sets `length = 0` once a frame. That makes it safe
 * to hand out recycled objects: the cursor resets the moment the queue is seen
 * empty, which happens exactly once per frame regardless of how many sim steps
 * ran inside it.
 *
 * That reset is a *hint*, not the bound. "The queue is empty" is a proxy for
 * "a new frame started", and it only holds while the caller drains in `main.ts`
 * order. A caller that drains once per *sim step* — every headless harness does
 * — pushes enemy fx before `beginStep` runs, so the queue is never seen empty,
 * the cursor never resets, and the pool grows by one object per particle for as
 * long as combat lasts. `tests/simcheck.test.ts` grew this array to 9.4 million
 * entries and took the worker out with a 4 GB heap OOM, which is why the pools
 * are rings now rather than lists that only ever grow. The cursor still resets
 * on an empty queue, so the common case reuses the same low slots and nothing
 * about the rendered result changes; the wrap is what makes the drain order
 * somebody else's business. Sizes match the equivalent rings in
 * `entities/Enemy.ts`, which has always been bounded this way.
 */
export class FxPool {
  /** Ring capacities. A frame never comes close; a frameless run cannot pass. */
  static readonly PARTICLE_RING = 4096;
  static readonly SHOCK_RING = 256;
  static readonly NUMBER_RING = 512;

  private particles: ParticleSpawn[] = [];
  private pCursor = 0;
  private shocks: Shockwave[] = [];
  private sCursor = 0;
  private numbers: { x: number; y: number; value: number; crit: boolean }[] = [];
  private nCursor = 0;

  /** Combat text emitted this step, so a pierce shot cannot flood the screen. */
  private numbersThisStep = 0;
  static readonly MAX_NUMBERS_PER_STEP = 24;

  /** Call at the top of every sim step. */
  beginStep(world: World): void {
    if (world.fx.particles.length === 0) this.pCursor = 0;
    if (world.fx.shockwaves.length === 0) this.sCursor = 0;
    if (world.fx.numbers.length === 0) this.nCursor = 0;
    this.numbersThisStep = 0;
  }

  particle(
    world: World, x: number, y: number, vx: number, vy: number,
    life: number, size: number, colour: PhosphorName,
    kind: ParticleSpawn['kind'], gain = 1, drag = 0, gravity = 0, spin = 0,
  ): void {
    if (this.pCursor >= FxPool.PARTICLE_RING) this.pCursor = 0;
    let p = this.particles[this.pCursor];
    if (!p) {
      p = { x: 0, y: 0, vx: 0, vy: 0, life: 0, size: 1, colour, kind, gain: 1, drag: 0, gravity: 0, spin: 0 };
      this.particles.push(p);
    }
    this.pCursor++;
    p.x = x; p.y = y; p.vx = vx; p.vy = vy;
    p.life = life; p.size = size; p.colour = colour; p.kind = kind;
    p.gain = gain; p.drag = drag; p.gravity = gravity; p.spin = spin;
    world.fx.particles.push(p);
  }

  shockwave(world: World, x: number, y: number, strength: number, radius: number, duration: number): void {
    if (this.sCursor >= FxPool.SHOCK_RING) this.sCursor = 0;
    let s = this.shocks[this.sCursor];
    if (!s) { s = { x: 0, y: 0, strength: 0, radius: 0, duration: 0 }; this.shocks.push(s); }
    this.sCursor++;
    s.x = x; s.y = y; s.strength = strength; s.radius = radius; s.duration = duration;
    world.fx.shockwaves.push(s);
  }

  number(world: World, x: number, y: number, value: number, crit: boolean): void {
    if (this.numbersThisStep >= FxPool.MAX_NUMBERS_PER_STEP && !crit) return;
    this.numbersThisStep++;
    if (this.nCursor >= FxPool.NUMBER_RING) this.nCursor = 0;
    let d = this.numbers[this.nCursor];
    if (!d) { d = { x: 0, y: 0, value: 0, crit: false }; this.numbers.push(d); }
    this.nCursor++;
    d.x = x; d.y = y; d.value = value; d.crit = crit;
    world.fx.numbers.push(d);
  }
}

// ─────────────────────────────────────────────────────────────────── trails ──

/**
 * Trail ribbon allocator. `render/TrailSystem.ts` belongs to another subsystem,
 * so the contract is declared here and `main.ts` injects the implementation.
 * The default hands back -1, which `Projectile.trail` already defines as
 * "untrailed", so the sim runs headless with no renderer at all.
 */
export interface TrailAllocator {
  acquire(colour: PhosphorName): number;
  release(slot: number): void;
}

export const NO_TRAILS: TrailAllocator = {
  acquire: () => -1,
  release: () => { /* nothing to give back */ },
};

// ──────────────────────────────────────────────────────────────── shot spec ──

export type ProjectileKind = 'bolt' | 'pellet' | 'arc' | 'orb' | 'well' | 'chain';

/**
 * Everything a tower needs to say about one shot. A single shared instance is
 * reused for every launch — `begin()` resets it, the caller overwrites the
 * fields it cares about, `launch()` copies it into the pool.
 */
export interface ShotSpec {
  kind: ProjectileKind;
  x: number; y: number;
  dirX: number; dirY: number;
  speed: number;
  ttl: number;
  damage: number;
  damageType: DamageType;
  pierce: number;
  splash: number;
  /** Fraction of damage still dealt at the very edge of the splash radius. */
  splashFalloff: number;
  armourPierce: number;
  critChance: number;
  critMultiplier: number;
  colour: PhosphorName;
  homing: number;
  targetId: number;
  ownerId: number;
  trailed: boolean;

  /** Ground-targeted arcs. */
  tx: number; ty: number; flight: number;

  chain: number; chainFalloff: number; chainRange: number;
  proxRadius: number;
  clusterCount: number; clusterFraction: number;

  dotDps: number; dotSecs: number; dotStacks: number;
  napalmDps: number; napalmSecs: number; napalmRadius: number;
  slowFactor: number; slowSecs: number;
  stunSecs: number; stunChance: number;
  shatterMul: number;
  shieldMelt: number;
  spreadRadius: number; spreadFraction: number;

  pullForce: number; wellLife: number;
  detonateFraction: number; detonateRadius: number;

  /** Cluster/spread recursion depth. Children may not spawn grandchildren. */
  generation: number;
}

/** Per-slot behaviour payload, parallel to the projectile pool. */
type Extra = Omit<ShotSpec, 'x' | 'y' | 'dirX' | 'dirY' | 'speed' | 'damage' | 'damageType'
  | 'pierce' | 'splash' | 'colour' | 'homing' | 'targetId' | 'ownerId' | 'ttl' | 'trailed'> & {
  elapsed: number;
};

function blankExtra(): Extra {
  return {
    kind: 'bolt',
    splashFalloff: 0.35, armourPierce: 1, critChance: 0, critMultiplier: 1.5,
    tx: 0, ty: 0, flight: 0, elapsed: 0,
    chain: 0, chainFalloff: 0, chainRange: 0,
    proxRadius: 0, clusterCount: 0, clusterFraction: 0,
    dotDps: 0, dotSecs: 0, dotStacks: 1,
    napalmDps: 0, napalmSecs: 0, napalmRadius: 0,
    slowFactor: 1, slowSecs: 0,
    stunSecs: 0, stunChance: 0,
    shatterMul: 1, shieldMelt: 1,
    spreadRadius: 0, spreadFraction: 0,
    pullForce: 0, wellLife: 0,
    detonateFraction: 0, detonateRadius: 0,
    generation: 0,
  };
}

// ─────────────────────────────────────────────────────────────────── system ──

/** Damage below this is discarded rather than rendered as a `0`. */
const MIN_MEANINGFUL = 0.05;
/** Hard ceiling on simultaneous DOT records per enemy. */
const MAX_DOT_SLOTS = 16;
/** Map diagonal, the widest a query ever needs to be. */
const MAP_DIAGONAL = Math.hypot(VIEW.width, VIEW.height);
/** Sweep radius for contact tests: the fattest enemy radius plus grace. */
const CONTACT_SWEEP = 24;

export class ProjectileSystem implements System {
  readonly name = 'projectiles';

  readonly fx = new FxPool();
  trails: TrailAllocator = NO_TRAILS;

  /**
   * Credit kills to `world.cash`. `Economy.ts` belongs to another subsystem; if
   * it wants to own income, `main.ts` sets this false and reads the per-tower
   * `damageDealt`/`kills` counters instead.
   */
  awardCash = true;
  /**
   * Tick damage-over-time here. Set false if the enemy simulation takes it over,
   * so a burning enemy is never billed twice in one step.
   */
  tickDots = true;

  /**
   * Per-tower cash multiplier from Pylon cash auras, keyed by tower id.
   * `TowerSystem` owns the contents; a missing entry means 1.0. Positional,
   * because a cash aura that paid out for towers outside the ring would make
   * the Pylon's third path meaningless.
   */
  readonly cashBonus = new Map<number, number>();

  private pool: Projectile[] = [];
  private extras: Extra[] = [];
  /** Free-slot stack. Popping beats scanning once the pool is a few hundred. */
  private free: number[] = [];
  private liveCount = 0;
  private nextId = 1;

  /** Owner lookup, refreshed when the tower roster changes. */
  private towerById = new Map<number, Tower>();
  private towerRosterSize = -1;

  /** Shared launch scratch. */
  private readonly spec: ShotSpec = {
    kind: 'bolt', x: 0, y: 0, dirX: 1, dirY: 0, speed: 300, ttl: 2,
    damage: 1, damageType: 'kinetic', pierce: 1, splash: 0, splashFalloff: 0.35,
    armourPierce: 1, critChance: 0, critMultiplier: 1.5, colour: 'yellow',
    homing: 0, targetId: -1, ownerId: -1, trailed: false,
    tx: 0, ty: 0, flight: 0,
    chain: 0, chainFalloff: 0, chainRange: 0,
    proxRadius: 0, clusterCount: 0, clusterFraction: 0,
    dotDps: 0, dotSecs: 0, dotStacks: 1,
    napalmDps: 0, napalmSecs: 0, napalmRadius: 0,
    slowFactor: 1, slowSecs: 0, stunSecs: 0, stunChance: 0,
    shatterMul: 1, shieldMelt: 1, spreadRadius: 0, spreadFraction: 0,
    pullForce: 0, wellLife: 0, detonateFraction: 0, detonateRadius: 0,
    generation: 0,
  };

  // Bound callbacks for the index sweeps. Built once.
  private readonly onSplash: (e: Enemy, i: number, d2: number) => void;
  private readonly onSpread: (e: Enemy, i: number, d2: number) => void;
  private readonly onPull: (e: Enemy, i: number, d2: number) => void;
  private readonly onProx: (e: Enemy, i: number, d2: number) => void;
  private readonly onContact: (e: Enemy, i: number, d2: number) => void;
  private readonly onBeam: (e: Enemy, i: number, d2: number) => void;

  // Contact-sweep state.
  private ctWorld: World | null = null;
  private ctProjectile: Projectile | null = null;
  private ctExtra: Extra | null = null;
  private ctOwner: Tower | undefined;
  private ctConsumed = false;
  /** Start of the current step's travel segment, and its squared length. */
  private ctFromX = 0; private ctFromY = 0; private ctSegLen2 = 0;

  // Beam-sweep state.
  private bmOx = 0; private bmOy = 0; private bmDx = 1; private bmDy = 0;
  private bmRange = 0; private bmWidth = 4;

  // Sweep state.
  private sw: World | null = null;
  private swOwner: Tower | undefined;
  private swX = 0; private swY = 0; private swRadius = 1;
  private swDamage = 0; private swType: DamageType = 'kinetic';
  private swFalloff = 0.35; private swExtra: Extra | null = null;
  private swPullForce = 0; private swDt = 0;
  private proxHit = false;

  constructor(private readonly index: EnemyIndex, capacity = 512) {
    for (let i = 0; i < capacity; i++) this.grow();
    this.onSplash = (e, _i, d2) => this.splashOne(e, d2);
    this.onSpread = (e, _i, _d2) => this.spreadOne(e);
    this.onPull = (e, _i, d2) => this.pullOne(e, d2);
    this.onProx = () => { this.proxHit = true; };
    this.onContact = (e, i, _d2) => this.onContactImpl(e, i);
    this.onBeam = (e, i, _d2) => this.onBeamImpl(e, i);
  }

  private grow(): void {
    this.pool.push({
      alive: false, id: 0,
      pos: { x: 0, y: 0 }, vel: { x: 0, y: 0 },
      damage: 0, damageType: 'kinetic', pierce: 0, splash: 0,
      hit: new Set<number>(), ttl: 0, colour: 'yellow',
      homing: 0, targetId: -1, ownerId: -1, trail: -1,
    });
    this.extras.push(blankExtra());
    this.free.push(this.pool.length - 1);
  }

  /** Live projectile count, for the debug overlay. */
  get active(): number { return this.liveCount; }

  // ───────────────────────────────────────────────────────────────── launch ──

  /** Resets and returns the shared spec. Fill it, then call `launch`. */
  begin(): ShotSpec {
    const s = this.spec;
    s.kind = 'bolt'; s.x = 0; s.y = 0; s.dirX = 1; s.dirY = 0;
    s.speed = 300; s.ttl = 2; s.damage = 1; s.damageType = 'kinetic';
    s.pierce = 1; s.splash = 0; s.splashFalloff = 0.35;
    s.armourPierce = 1; s.critChance = 0; s.critMultiplier = 1.5;
    s.colour = 'yellow'; s.homing = 0; s.targetId = -1; s.ownerId = -1;
    s.trailed = false;
    s.tx = 0; s.ty = 0; s.flight = 0;
    s.chain = 0; s.chainFalloff = 0; s.chainRange = 0;
    s.proxRadius = 0; s.clusterCount = 0; s.clusterFraction = 0;
    s.dotDps = 0; s.dotSecs = 0; s.dotStacks = 1;
    s.napalmDps = 0; s.napalmSecs = 0; s.napalmRadius = 0;
    s.slowFactor = 1; s.slowSecs = 0; s.stunSecs = 0; s.stunChance = 0;
    s.shatterMul = 1; s.shieldMelt = 1; s.spreadRadius = 0; s.spreadFraction = 0;
    s.pullForce = 0; s.wellLife = 0; s.detonateFraction = 0; s.detonateRadius = 0;
    s.generation = 0;
    return s;
  }

  /** Copies the current spec into a pooled projectile. */
  launch(): void {
    const s = this.spec;
    let slot = this.free.pop();
    if (slot === undefined) { this.grow(); slot = this.free.pop()!; }
    this.liveCount++;

    const p = this.pool[slot];
    const x = this.extras[slot];

    p.alive = true;
    p.id = this.nextId++;
    p.pos.x = s.x; p.pos.y = s.y;
    p.vel.x = s.dirX * s.speed; p.vel.y = s.dirY * s.speed;
    p.damage = s.damage; p.damageType = s.damageType;
    p.pierce = s.pierce; p.splash = s.splash;
    p.hit.clear();
    p.ttl = s.ttl; p.colour = s.colour;
    p.homing = s.homing; p.targetId = s.targetId; p.ownerId = s.ownerId;
    p.trail = s.trailed ? this.trails.acquire(s.colour) : -1;

    x.kind = s.kind;
    x.splashFalloff = s.splashFalloff;
    x.armourPierce = s.armourPierce;
    x.critChance = s.critChance; x.critMultiplier = s.critMultiplier;
    x.tx = s.tx; x.ty = s.ty; x.flight = s.flight; x.elapsed = 0;
    x.chain = s.chain; x.chainFalloff = s.chainFalloff; x.chainRange = s.chainRange;
    x.proxRadius = s.proxRadius;
    x.clusterCount = s.clusterCount; x.clusterFraction = s.clusterFraction;
    x.dotDps = s.dotDps; x.dotSecs = s.dotSecs; x.dotStacks = s.dotStacks;
    x.napalmDps = s.napalmDps; x.napalmSecs = s.napalmSecs; x.napalmRadius = s.napalmRadius;
    x.slowFactor = s.slowFactor; x.slowSecs = s.slowSecs;
    x.stunSecs = s.stunSecs; x.stunChance = s.stunChance;
    x.shatterMul = s.shatterMul; x.shieldMelt = s.shieldMelt;
    x.spreadRadius = s.spreadRadius; x.spreadFraction = s.spreadFraction;
    x.pullForce = s.pullForce; x.wellLife = s.wellLife;
    x.detonateFraction = s.detonateFraction; x.detonateRadius = s.detonateRadius;
    x.generation = s.generation;
  }

  private retire(slot: number): void {
    const p = this.pool[slot];
    if (!p.alive) return;
    p.alive = false;
    this.liveCount--;
    if (p.trail >= 0) { this.trails.release(p.trail); p.trail = -1; }
    p.hit.clear();
    this.free.push(slot);
  }

  // ─────────────────────────────────────────────────────────────────── step ──

  step(dt: number, world: World): void {
    this.fx.beginStep(world);
    this.refreshOwners(world);
    if (this.tickDots) this.stepDots(dt, world);

    const pool = this.pool;
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      if (!p.alive) continue;
      const x = this.extras[i];

      p.ttl -= dt;
      if (p.ttl <= 0) {
        if (x.kind === 'well') this.collapseWell(world, p, x);
        else if (x.kind === 'arc') this.detonate(world, p, x, p.pos.x, p.pos.y);
        this.retire(i);
        continue;
      }

      switch (x.kind) {
        case 'arc': this.stepArc(dt, world, p, x, i); break;
        case 'well': this.stepWell(dt, world, p, x); break;
        default: this.stepFlying(dt, world, p, x, i); break;
      }
    }
  }

  private refreshOwners(world: World): void {
    if (world.towers.length === this.towerRosterSize) return;
    this.towerRosterSize = world.towers.length;
    this.towerById.clear();
    for (let i = 0; i < world.towers.length; i++) {
      this.towerById.set(world.towers[i].id, world.towers[i]);
    }
  }

  // ──────────────────────────────────────────────────────── flight handlers ──

  private stepFlying(dt: number, world: World, p: Projectile, x: Extra, slot: number): void {
    // Homing: bend the velocity toward the tracked enemy without changing speed.
    if (p.homing > 0 && p.targetId >= 0) {
      const t = this.enemyById(p.targetId);
      if (t && t.alive) {
        const dx = t.pos.x - p.pos.x, dy = t.pos.y - p.pos.y;
        const d = Math.hypot(dx, dy) || 1;
        const speed = Math.hypot(p.vel.x, p.vel.y) || 1;
        const k = Math.min(1, p.homing * dt * 6);
        p.vel.x += (dx / d * speed - p.vel.x) * k;
        p.vel.y += (dy / d * speed - p.vel.y) * k;
        const s2 = Math.hypot(p.vel.x, p.vel.y) || 1;
        p.vel.x = p.vel.x / s2 * speed;
        p.vel.y = p.vel.y / s2 * speed;
      }
    }

    const fromX = p.pos.x, fromY = p.pos.y;
    p.pos.x += p.vel.x * dt;
    p.pos.y += p.vel.y * dt;

    if (p.pos.x < -32 || p.pos.y < -32 || p.pos.x > VIEW.width + 32 || p.pos.y > VIEW.height + 32) {
      this.retire(slot);
      return;
    }

    // Proximity fuse: burst near the target rather than waiting for contact.
    if (x.proxRadius > 0) {
      this.proxHit = false;
      this.index.forEachInRadius(p.pos.x, p.pos.y, x.proxRadius, this.onProx);
      if (this.proxHit) {
        this.detonate(world, p, x, p.pos.x, p.pos.y);
        this.retire(slot);
        return;
      }
    }

    // Contact, tested against the whole step's travel rather than the end
    // point. A Sniper round covers 23px in one step and an enemy is 5px wide,
    // so a point test would let three quarters of the map's fastest shots pass
    // straight through their target.
    this.ctWorld = world;
    this.ctProjectile = p;
    this.ctExtra = x;
    this.ctOwner = this.ownerOf(p.ownerId);
    this.ctConsumed = false;
    this.ctFromX = fromX; this.ctFromY = fromY;
    const segX = p.pos.x - fromX, segY = p.pos.y - fromY;
    this.ctSegLen2 = segX * segX + segY * segY;
    this.index.forEachInRadius(
      (fromX + p.pos.x) * 0.5, (fromY + p.pos.y) * 0.5,
      Math.sqrt(this.ctSegLen2) * 0.5 + CONTACT_SWEEP, this.onContact,
    );
    this.ctWorld = null; this.ctProjectile = null; this.ctExtra = null;

    if (this.ctConsumed) {
      if (p.splash > 0) this.detonate(world, p, x, p.pos.x, p.pos.y);
      this.retire(slot);
    }
  }

  private onContactImpl(e: Enemy, ei: number): void {
    const world = this.ctWorld, p = this.ctProjectile, x = this.ctExtra;
    if (!world || !p || !x || this.ctConsumed) return;
    if (!e.alive || p.hit.has(e.id)) return;

    // Closest approach between the enemy centre and this step's travel segment.
    const sx = p.pos.x - this.ctFromX, sy = p.pos.y - this.ctFromY;
    const ex = e.pos.x - this.ctFromX, ey = e.pos.y - this.ctFromY;
    let t = this.ctSegLen2 > 0 ? (ex * sx + ey * sy) / this.ctSegLen2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const dx = ex - sx * t, dy = ey - sy * t;
    const r = e.archetype.radius + 1;
    if (dx * dx + dy * dy > r * r) return;

    p.hit.add(e.id);
    this.impact(world, p, x, e, ei, this.ctOwner);
    p.pierce--;
    if (p.pierce <= 0) this.ctConsumed = true;
  }

  private stepArc(dt: number, world: World, p: Projectile, x: Extra, slot: number): void {
    x.elapsed += dt;
    const t = x.flight > 0 ? Math.min(1, x.elapsed / x.flight) : 1;
    p.pos.x += p.vel.x * dt;
    p.pos.y += p.vel.y * dt;
    if (t >= 1) {
      this.detonate(world, p, x, x.tx, x.ty);
      this.retire(slot);
    }
  }

  private stepWell(dt: number, world: World, p: Projectile, x: Extra): void {
    // The well is stationary once armed; it pulls, grinds, and warps the grid.
    this.swDt = dt;
    this.swPullForce = x.pullForce;
    this.swX = p.pos.x; this.swY = p.pos.y; this.swRadius = Math.max(1, p.splash);
    this.sw = world;
    this.swOwner = this.ownerOf(p.ownerId);
    this.swDamage = p.damage * dt;
    this.swType = p.damageType;
    this.swFalloff = 1;
    this.swExtra = x;
    this.index.forEachInRadius(p.pos.x, p.pos.y, this.swRadius, this.onPull);
    this.sw = null; this.swExtra = null;

    // Negative strength: `core/types.ts` defines that as an inward pull, which
    // is exactly the lattice deformation a gravity well should read as.
    this.fx.shockwave(world, p.pos.x, p.pos.y, -x.pullForce * 0.0015 - 0.4, this.swRadius, dt);
    if ((world.tick & 3) === 0) {
      const a = world.rng() * Math.PI * 2;
      const r = this.swRadius * (0.6 + world.rng() * 0.4);
      this.fx.particle(world, p.pos.x + Math.cos(a) * r, p.pos.y + Math.sin(a) * r,
        -Math.cos(a) * 60, -Math.sin(a) * 60, 0.5, 1, p.colour, 'spark', 2.2, 0.4);
    }
  }

  private pullOne(e: Enemy, d2: number): void {
    const world = this.sw;
    const x = this.swExtra;
    if (!world || !x) return;
    const dx = this.swX - e.pos.x, dy = this.swY - e.pos.y;
    const d = Math.sqrt(d2) || 1;
    // Inverse-linear falloff, capped so a boss is not teleported into the core.
    const a = Math.min(this.swPullForce, this.swPullForce * (1 - d / this.swRadius));
    const immune = e.archetype.immune;
    if (!immune || immune.indexOf('knockback') < 0) {
      const push = Math.min(a * this.swDt, d);
      e.pos.x += dx / d * push;
      e.pos.y += dy / d * push;
      e.vel.x += dx / d * a * this.swDt * 0.4;
      e.vel.y += dy / d * a * this.swDt * 0.4;
    }
    if (x.slowSecs > 0) this.applySlow(world, e, x.slowFactor, x.slowSecs);
    if (this.swDamage > 0) {
      this.resolve(world, e, this.swDamage, this.swType, x.armourPierce, x.shieldMelt,
        false, 1, this.swOwner, false);
    }
  }

  private collapseWell(world: World, p: Projectile, x: Extra): void {
    if (x.detonateFraction <= 0) return;
    const radius = x.detonateRadius > 0 ? x.detonateRadius : Math.max(1, p.splash);
    this.blast(world, p.pos.x, p.pos.y, p.damage * x.detonateFraction, p.damageType,
      radius, 0.5, x, this.ownerOf(p.ownerId), p.colour);
    world.fx.shake += Math.min(6, radius * 0.05);
    this.fx.shockwave(world, p.pos.x, p.pos.y, 2.6, radius * 1.3, 0.2);
  }

  // ─────────────────────────────────────────────────────────────── impacts ──

  /** Direct contact: full damage plus every rider the shot is carrying. */
  private impact(world: World, p: Projectile, x: Extra, e: Enemy, ei: number, owner: Tower | undefined): void {
    const crit = x.critChance > 0 && world.rng() < x.critChance;
    let mul = 1;
    // Shatter: cold-softened targets take a multiplier from the same shot.
    if (x.shatterMul > 1 && (e.slowUntil > world.time || e.stunUntil > world.time)) mul = x.shatterMul;

    const dealt = this.resolve(world, e, p.damage * mul, p.damageType,
      x.armourPierce, x.shieldMelt, crit, x.critMultiplier, owner, true);

    this.applyRiders(world, e, x);

    // Chain lightning walks outward from the point of contact.
    if (x.chain > 0) this.chainFrom(world, p, x, e, owner);

    // Spread-on-death: if this hit killed the host, seed the neighbours.
    if (!e.alive && x.spreadRadius > 0 && x.generation === 0) {
      this.swExtra = x; this.sw = world;
      this.index.forEachInRadius(e.pos.x, e.pos.y, x.spreadRadius, this.onSpread, e.id);
      this.swExtra = null; this.sw = null;
    }

    // A busy board resolves four or five of these a frame and nothing else, so
    // this burst — not the rare explosion — is what a firefight actually looks
    // like. At four sparks it emitted twenty particles a frame across a
    // twelve-tower board and the fight measured 1.6 percentage points brighter
    // than an empty screen. The pool holds 24,000.
    const hitColour = crit ? 'white' : p.colour;
    const sparks = crit ? 22 : 12;
    for (let k = 0; k < sparks; k++) {
      const a = world.rng() * Math.PI * 2;
      const s = 30 + world.rng() * 120;
      this.fx.particle(world, e.pos.x, e.pos.y, Math.cos(a) * s, Math.sin(a) * s,
        0.20 + world.rng() * 0.18, 1, hitColour, 'spark', crit ? 4.4 : 2.6, 3.2);
    }
    this.fx.particle(world, e.pos.x, e.pos.y, 0, 0, 0.14,
      e.archetype.radius * 2.0, hitColour, 'ring', crit ? 3.4 : 2.2);
    // Two frames of contact glow. Every shot in the game passes through here,
    // so it is deliberately small and deliberately brief — this is the term
    // that makes a firefight read as continuous light rather than as confetti.
    this.fx.particle(world, e.pos.x, e.pos.y, 0, 0, 0.08,
      e.archetype.radius * 3.2, hitColour, 'smoke', crit ? 5.0 : 3.0);
    if (dealt > MIN_MEANINGFUL) this.fx.number(world, e.pos.x, e.pos.y - 6, dealt, crit);
    void ei;
  }

  /** Status effects carried by a projectile, applied on any kind of contact. */
  private applyRiders(world: World, e: Enemy, x: Extra): void {
    if (x.slowSecs > 0) this.applySlow(world, e, x.slowFactor, x.slowSecs);
    if (x.stunSecs > 0 && (x.stunChance >= 1 || world.rng() < x.stunChance)) {
      this.applyStun(world, e, x.stunSecs);
    }
    if (x.dotDps > 0) applyDot(e, x.dotDps, x.dotSecs, this.dotType(x), x.dotStacks, world.time);
  }

  private dotType(x: Extra): DamageType {
    return x.napalmDps > 0 ? 'explosive' : 'corrosive';
  }

  private spreadOne(e: Enemy): void {
    const world = this.sw; const x = this.swExtra;
    if (!world || !x) return;
    applyDot(e, x.dotDps * x.spreadFraction, x.dotSecs, this.dotType(x), x.dotStacks, world.time);
    this.fx.particle(world, e.pos.x, e.pos.y, 0, -18, 0.4, 1, 'green', 'smoke', 1.4, 1.5);
  }

  /** Detonation point of an arc, a fused pellet, or a splashing bolt. */
  private detonate(world: World, p: Projectile, x: Extra, cx: number, cy: number): void {
    const radius = Math.max(p.splash, 1);
    const owner = this.ownerOf(p.ownerId);
    this.blast(world, cx, cy, p.damage, p.damageType, radius, x.splashFalloff, x, owner, p.colour);

    // Bomblets. Second generation only — no recursive fireworks.
    if (x.clusterCount > 0 && x.generation === 0) {
      const n = x.clusterCount;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + world.rng() * 0.6;
        const r = radius * (0.5 + world.rng() * 1.1);
        const s = this.begin();
        s.kind = 'arc';
        s.x = cx; s.y = cy;
        s.tx = cx + Math.cos(a) * r; s.ty = cy + Math.sin(a) * r;
        s.flight = 0.22 + world.rng() * 0.16;
        s.dirX = (s.tx - s.x) / s.flight; s.dirY = (s.ty - s.y) / s.flight;
        s.speed = 1; s.ttl = s.flight + 0.05;
        s.damage = p.damage * x.clusterFraction;
        s.damageType = p.damageType;
        s.pierce = 1;
        s.splash = radius * 0.55;
        s.splashFalloff = x.splashFalloff;
        s.armourPierce = x.armourPierce;
        s.colour = p.colour;
        s.ownerId = p.ownerId;
        s.napalmDps = x.napalmDps * 0.4; s.napalmSecs = x.napalmSecs; s.napalmRadius = x.napalmRadius * 0.6;
        s.dotDps = x.dotDps * 0.5; s.dotSecs = x.dotSecs; s.dotStacks = x.dotStacks;
        s.generation = 1;
        this.launch();
      }
    }

    // Napalm: a lingering ground fire, expressed as a slow-burning well that
    // does no pulling. Cheaper than a bespoke entity and it reuses the pool.
    if (x.napalmDps > 0 && x.generation === 0) {
      const s = this.begin();
      s.kind = 'well';
      s.x = cx; s.y = cy;
      s.speed = 0; s.dirX = 0; s.dirY = 0;
      s.ttl = x.napalmSecs;
      s.damage = x.napalmDps;
      s.damageType = 'explosive';
      s.splash = x.napalmRadius;
      s.pierce = 9999;
      s.colour = 'red';
      s.ownerId = p.ownerId;
      s.armourPierce = x.armourPierce;
      s.pullForce = 0;
      s.dotDps = x.dotDps; s.dotSecs = x.dotSecs; s.dotStacks = x.dotStacks;
      s.generation = 1;
      this.launch();
    }

    // Gravity wells are delivered, not conjured: the Singularity fires an arc
    // and the well arms where it lands.
    if (x.wellLife > 0 && x.generation === 0) {
      const s = this.begin();
      s.kind = 'well';
      s.x = cx; s.y = cy;
      s.speed = 0; s.dirX = 0; s.dirY = 0;
      s.ttl = x.wellLife;
      s.damage = p.damage;
      s.damageType = p.damageType;
      s.splash = radius;
      s.pierce = 9999;
      s.colour = p.colour;
      s.ownerId = p.ownerId;
      s.armourPierce = x.armourPierce;
      s.shieldMelt = x.shieldMelt;
      s.pullForce = x.pullForce;
      s.slowFactor = x.slowFactor; s.slowSecs = x.slowSecs;
      s.dotDps = x.dotDps; s.dotSecs = x.dotSecs; s.dotStacks = x.dotStacks;
      s.detonateFraction = x.detonateFraction; s.detonateRadius = x.detonateRadius;
      s.generation = 1;
      this.launch();
    }

    // Explosion FX. Core flash, ring, embers, hue halo, then a lattice shove.
    //
    // The flash and the halo are what produce a bloom *skirt* rather than a
    // 3px glow. A point emitter fills only the tightest pyramid levels, so no
    // amount of gain on a 2px spark buys reach; a soft disc 1.4x the blast
    // radius fills the wide levels too, which is how gwre2_deadline-bloom-skirt
    // gets a hot white core grading out through hue to a halo. The flash lives
    // 6 frames — long enough to read, short enough that the frame is dark again
    // before the next shot.
    this.fx.particle(world, cx, cy, 0, 0, 0.12, radius * 2.0, 'white', 'smoke', 6.0);
    this.fx.particle(world, cx, cy, 0, 0, 0.26, radius, 'white', 'ring', 3.2);
    const halo = 4 + (radius > 16 ? 4 : 0);
    for (let i = 0; i < halo; i++) {
      const a = world.rng() * Math.PI * 2;
      const sp = 20 + world.rng() * 46;
      this.fx.particle(world, cx, cy, Math.cos(a) * sp, Math.sin(a) * sp,
        0.22 + world.rng() * 0.26, radius * (0.9 + world.rng() * 0.8),
        p.colour, 'smoke', 3.4, 1.6, -10);
    }
    const embers = Math.min(90, 24 + (radius | 0) * 4);
    for (let i = 0; i < embers; i++) {
      const a = world.rng() * Math.PI * 2;
      const sp = 40 + world.rng() * radius * 7;
      this.fx.particle(world, cx, cy, Math.cos(a) * sp, Math.sin(a) * sp,
        0.3 + world.rng() * 0.3, 1 + world.rng() * 2, p.colour, 'debris', 3.4, 2.4, 60,
        (world.rng() - 0.5) * 12);
    }
    this.fx.shockwave(world, cx, cy, 1.6 + radius * 0.05, radius * 1.6, 0.12);
    world.fx.shake += Math.min(4, radius * 0.035);
  }

  /** Radial damage with linear falloff to `falloff` at the rim. */
  private blast(
    world: World, cx: number, cy: number, damage: number, type: DamageType,
    radius: number, falloff: number, x: Extra, owner: Tower | undefined, colour: PhosphorName,
  ): void {
    this.sw = world; this.swOwner = owner;
    this.swX = cx; this.swY = cy; this.swRadius = Math.max(1, radius);
    this.swDamage = damage; this.swType = type; this.swFalloff = falloff;
    this.swExtra = x;
    this.index.forEachInRadius(cx, cy, radius, this.onSplash);
    this.sw = null; this.swExtra = null;
    void colour;
  }

  private splashOne(e: Enemy, d2: number): void {
    const world = this.sw; const x = this.swExtra;
    if (!world || !x) return;
    const d = Math.sqrt(d2);
    const t = Math.min(1, d / this.swRadius);
    const scale = 1 + (this.swFalloff - 1) * t;
    const crit = x.critChance > 0 && world.rng() < x.critChance;
    let mul = 1;
    if (x.shatterMul > 1 && (e.slowUntil > world.time || e.stunUntil > world.time)) mul = x.shatterMul;
    const dealt = this.resolve(world, e, this.swDamage * scale * mul, this.swType,
      x.armourPierce, x.shieldMelt, crit, x.critMultiplier, this.swOwner, true);
    this.applyRiders(world, e, x);
    if (dealt > MIN_MEANINGFUL) this.fx.number(world, e.pos.x, e.pos.y - 6, dealt, crit);
  }

  // ────────────────────────────────────────────────────────────────── chain ──

  /** Walks from `from` to the nearest untouched neighbour, `x.chain` times. */
  private chainFrom(world: World, p: Projectile, x: Extra, from: Enemy, owner: Tower | undefined): void {
    let cx = from.pos.x, cy = from.pos.y;
    let damage = p.damage;
    const reach = x.chainRange > 0 ? x.chainRange : 48;
    let prevId = from.id;

    for (let jump = 0; jump < x.chain; jump++) {
      damage *= x.chainFalloff > 0 ? x.chainFalloff : 0.7;
      if (damage < MIN_MEANINGFUL) return;

      const idx = this.index.nearest(cx, cy, reach, prevId);
      if (idx === NO_TARGET) return;
      const e = this.index.list[idx];
      if (p.hit.has(e.id)) return;
      p.hit.add(e.id);

      const crit = x.critChance > 0 && world.rng() < x.critChance;
      const dealt = this.resolve(world, e, damage, p.damageType,
        x.armourPierce, x.shieldMelt, crit, x.critMultiplier, owner, true);
      this.applyRiders(world, e, x);

      // The arc itself: a short spark line the particle system draws as a bolt.
      const dx = e.pos.x - cx, dy = e.pos.y - cy;
      const segs = 4;
      for (let k = 1; k <= segs; k++) {
        const f = k / segs;
        this.fx.particle(world, cx + dx * f + (world.rng() - 0.5) * 4,
          cy + dy * f + (world.rng() - 0.5) * 4, 0, 0, 0.09, 1, p.colour, 'spark', 3.4);
      }
      if (dealt > MIN_MEANINGFUL) this.fx.number(world, e.pos.x, e.pos.y - 6, dealt, crit);

      prevId = e.id;
      cx = e.pos.x; cy = e.pos.y;
    }
  }

  // ──────────────────────────────────────────────────────────────── hitscan ──

  /** Scratch for beam sweeps. Grown on demand, never per call. */
  private beamIdx = new Int32Array(64);
  private beamDist = new Float32Array(64);
  private beamCount = 0;

  /** Gathers one beam candidate: on the segment, inside the beam half-width. */
  private onBeamImpl(e: Enemy, ei: number): void {
    const rx = e.pos.x - this.bmOx, ry = e.pos.y - this.bmOy;
    const along = rx * this.bmDx + ry * this.bmDy;
    if (along < 0 || along > this.bmRange) return;
    const perp = Math.abs(rx * this.bmDy - ry * this.bmDx);
    if (perp > this.bmWidth + e.archetype.radius) return;
    if (this.beamCount >= this.beamIdx.length) {
      const bi = new Int32Array(this.beamIdx.length * 2);
      const bd = new Float32Array(this.beamIdx.length * 2);
      bi.set(this.beamIdx); bd.set(this.beamDist);
      this.beamIdx = bi; this.beamDist = bd;
    }
    this.beamIdx[this.beamCount] = ei;
    this.beamDist[this.beamCount] = along;
    this.beamCount++;
  }

  /**
   * Instant beam from a tower along a direction. Damages up to `pierce`
   * enemies in near-to-far order and returns the distance the beam travelled
   * before it ran out of targets, so the renderer knows how long to draw it.
   */
  hitscan(
    world: World, ox: number, oy: number, dx: number, dy: number, range: number,
    damage: number, type: DamageType, pierce: number, armourPierce: number,
    shieldMelt: number, critChance: number, critMultiplier: number,
    colour: PhosphorName, owner: Tower | undefined, beamWidth = 4,
  ): number {
    this.beamCount = 0;
    const mx = ox + dx * range * 0.5;
    const my = oy + dy * range * 0.5;
    const sweep = range * 0.5 + beamWidth + 8;

    this.bmOx = ox; this.bmOy = oy; this.bmDx = dx; this.bmDy = dy;
    this.bmRange = range; this.bmWidth = beamWidth;
    this.index.forEachInRadius(mx, my, sweep, this.onBeam);

    let reached = range;
    const hits = Math.min(pierce, this.beamCount);
    // Partial selection sort: we only ever need the `pierce` nearest.
    for (let k = 0; k < hits; k++) {
      let best = k;
      for (let j = k + 1; j < this.beamCount; j++) {
        if (this.beamDist[j] < this.beamDist[best]) best = j;
      }
      if (best !== k) {
        const ti = this.beamIdx[k]; this.beamIdx[k] = this.beamIdx[best]; this.beamIdx[best] = ti;
        const td = this.beamDist[k]; this.beamDist[k] = this.beamDist[best]; this.beamDist[best] = td;
      }
      const e = this.index.list[this.beamIdx[k]];
      if (!e || !e.alive) continue;
      const crit = critChance > 0 && world.rng() < critChance;
      const dealt = this.resolve(world, e, damage, type, armourPierce, shieldMelt,
        crit, critMultiplier, owner, true);
      if (dealt > MIN_MEANINGFUL) this.fx.number(world, e.pos.x, e.pos.y - 6, dealt, crit);
      this.fx.particle(world, e.pos.x, e.pos.y, 0, 0, 0.12, 2, colour, 'spark', 3.6);
      reached = Math.max(this.beamDist[k], 12);
    }
    if (this.beamCount === 0) reached = range;

    return reached;
  }

  // ──────────────────────────────────────────────── damage + status effects ──

  /**
   * The one place hit points change. Returns the hit points actually removed,
   * which is what the damage counter, the cash award and the floating number
   * all agree to use.
   */
  resolve(
    world: World, e: Enemy, amount: number, type: DamageType,
    armourPierce: number, shieldMelt: number, crit: boolean, critMultiplier: number,
    owner: Tower | undefined, flash: boolean,
  ): number {
    if (!e.alive || amount <= 0) return 0;

    // 1. resistance by damage type
    const resist = e.archetype.resist;
    let dmg = amount * (resist && resist[type] !== undefined ? resist[type]! : 1);

    // 2/3. armour: the pierce bonus only exists because armour exists
    const armour = e.archetype.armour;
    if (armour > 0) {
      dmg *= armourPierce;
      dmg -= armour;
      if (dmg < 0) dmg = 0;
    }

    // 4. crit, on what got through
    if (crit) dmg *= critMultiplier;
    if (dmg <= 0) return 0;

    // 5. shield, which melts at its own rate
    let absorbed = 0;
    if (e.shield > 0) {
      const asShield = dmg * shieldMelt;
      absorbed = Math.min(e.shield, asShield);
      e.shield -= absorbed;
      dmg = (asShield - absorbed) / (shieldMelt || 1);
      if (e.shield < 0) e.shield = 0;
    }

    // 6. hit points
    let removed = 0;
    if (dmg > 0) {
      const before = e.hp;
      e.hp -= dmg;
      if (e.hp < 0) e.hp = 0;
      removed = before - e.hp;
    }
    if (flash) e.flash = 1;

    const scored = removed + absorbed;
    if (owner) owner.damageDealt += scored;
    if (this.awardCash && removed > 0) {
      const bonus = owner ? (this.cashBonus.get(owner.id) ?? 1) : 1;
      world.cash += removed * ECONOMY.cashPerHp * payoutScale(world.round)
        * e.archetype.bounty * bonus;
    }

    if (e.hp <= 0 && e.alive) {
      e.alive = false;
      if (owner) owner.kills++;
      this.deathFx(world, e);
    }
    return scored;
  }

  /**
   * A shot kill's effects.
   *
   * This is *the* death the player sees: `resolve` handles every kill a real
   * board makes, and `Enemy.killEnemy` only catches the damage-over-time tail.
   * So the composition lives with the enemy pool, where the per-instance seed
   * that chooses between the six death variants lives, and where the flash a
   * body coming apart owes the lattice and the camera is charged. Drawing a
   * second, differently-shaped death here would mean a kill looked different
   * depending on which of two files noticed it first.
   */
  private deathFx(world: World, e: Enemy): void {
    deathBurst(world, e);
    // A death is the commonest event in the game, so its flash is scaled off
    // the body rather than fixed: a swarmling pops, a colossus lights the room.
    this.fx.particle(world, e.pos.x, e.pos.y, 0, 0, 0.13, e.archetype.radius * 6.0,
      e.archetype.colour, 'smoke', 5.0);
  }

  /** Public so the Cryo permafrost aura can chill without firing a shot. */
  applySlow(world: World, e: Enemy, factor: number, secs: number): void {
    const immune = e.archetype.immune;
    if (immune && immune.indexOf('slow') >= 0) return;
    const until = world.time + secs;
    // A stronger slow always wins; an equal one just refreshes the timer.
    if (factor < e.slowFactor || e.slowUntil <= world.time) e.slowFactor = factor;
    if (until > e.slowUntil) e.slowUntil = until;
  }

  private applyStun(world: World, e: Enemy, secs: number): void {
    const immune = e.archetype.immune;
    if (immune && immune.indexOf('stun') >= 0) return;
    const until = world.time + secs;
    if (until > e.stunUntil) e.stunUntil = until;
  }

  /** Burns down every active DOT record. Idempotent per step. */
  private stepDots(dt: number, world: World): void {
    const list = this.index.list;
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (!e || !e.alive) continue;
      const dots = e.dots;
      if (dots.length === 0) continue;
      const immune = e.archetype.immune;
      if (immune && immune.indexOf('dot') >= 0) { dots.length = 0; continue; }
      for (let k = 0; k < dots.length; k++) {
        const d = dots[k];
        if (d.until <= world.time || d.dps <= 0) continue;
        this.resolve(world, e, d.dps * dt, d.type, 1, 1, false, 1, undefined, false);
        if (!e.alive) break;
      }
    }
  }

  private ownerOf(id: number): Tower | undefined {
    return id < 0 ? undefined : this.towerById.get(id);
  }

  private enemyById(id: number): Enemy | undefined {
    return this.index.find(id);
  }

  /** Widest query the sim ever needs; exported for the sniper's `global` grant. */
  static readonly MAX_RANGE = MAP_DIAGONAL;
}

/**
 * Adds or refreshes a damage-over-time record.
 *
 * Stacking is capped per damage type. Once the cap is reached a new application
 * refreshes the weakest existing stack instead of adding a slot, so an Acid
 * Sprayer at ten stacks keeps its damage ceiling honest rather than growing
 * without bound as fire rate climbs.
 */
export function applyDot(
  e: Enemy, dps: number, secs: number, type: DamageType, maxStacks: number, now: number,
): void {
  if (dps <= 0 || secs <= 0) return;
  const immune = e.archetype.immune;
  if (immune && immune.indexOf('dot') >= 0) return;

  const dots = e.dots;
  let sameCount = 0, weakest = -1, weakestDps = Infinity, free = -1;
  for (let i = 0; i < dots.length; i++) {
    const d = dots[i];
    if (d.until <= now) { if (free < 0) free = i; continue; }
    if (d.type !== type) continue;
    sameCount++;
    if (d.dps < weakestDps) { weakestDps = d.dps; weakest = i; }
  }

  if (sameCount >= Math.max(1, maxStacks)) {
    if (weakest >= 0 && dps >= weakestDps) {
      dots[weakest].dps = dps;
      dots[weakest].until = now + secs;
    }
    return;
  }
  if (free >= 0) {
    dots[free].dps = dps; dots[free].until = now + secs; dots[free].type = type;
    return;
  }
  if (dots.length < MAX_DOT_SLOTS) dots.push({ dps, until: now + secs, type });
}
